import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Building2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Actions as ORG_ACTIONS } from '../../store/actions/organisationActions';
import {
  billingRelationshipLabel,
  getClientUnusableReason,
  getConsultantClientAccessLockMessage,
  isClientUsable,
  planTypeLabel,
  resolveClientPriceLabel,
  resolveCommitmentEndLabel,
  statusBadgeConfig,
} from '../utils/consultantClientUtils';

const ConsultantClientDetail = ({ clients, renewals, schedules, masterPerReportFeeVal, onBack, setCurrentOrganisation }) => {
  const { id } = useParams();
  const client = clients.find((c) => c.id === id);
  const renewal = (renewals || []).find((r) => r.organisationId === id);
  const schedule = (schedules || []).find((s) => s.organisationId === id);
  const currency = client?.currency || '£';

  if (!client) {
    return (
      <div className='flex flex-col gap-4'>
        <Button variant='ghost' size='sm' onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to portfolio
        </Button>
        <p className='text-muted-foreground'>Client not found.</p>
      </div>
    );
  }

  const statusCfg = statusBadgeConfig(client);
  const usable = isClientUsable(client, renewal);
  const unusableReason = getClientUnusableReason(client);
  const accessLockMessage = getConsultantClientAccessLockMessage(client);

  const handleOpenOrg = () => {
    setCurrentOrganisation(client);
    localStorage.setItem('Organisation', client.id);
    window.location.href = '/reports';
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-3'>
        <Button variant='ghost' size='sm' onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.5} />
        </Button>
        <div className='flex-1'>
          <h2 className='text-lg font-semibold flex items-center gap-2 text-foreground'>
            <Building2 size={20} strokeWidth={1.5} />
            {client.name || client.accountName}
          </h2>
          <p className='text-sm text-muted-foreground'>Client billing and access summary</p>
        </div>
        <Button size='sm' onClick={handleOpenOrg}>
          <ExternalLink size={14} strokeWidth={1.5} />
          Open client workspace
        </Button>
      </div>

      {accessLockMessage && (
        <Alert variant='warning'>
          <AlertDescription className='text-foreground'>{accessLockMessage}</AlertDescription>
        </Alert>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-base'>Lifecycle</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            <div className='flex justify-between items-center'>
              <span className='text-muted-foreground'>Status</span>
              <Badge className={statusCfg.className}>{statusCfg.label}</Badge>
            </div>
            <div className='flex justify-between'>
              <span className='text-muted-foreground'>Billing</span>
              <span className='text-foreground'>{billingRelationshipLabel(client.billingRelationship)}</span>
            </div>
            <div className='flex justify-between'>
              <span className='text-muted-foreground'>Report generation flag</span>
              <span className='text-foreground'>{client.reportGeneration === false ? 'Disabled' : 'Enabled'}</span>
            </div>
            {client.billingRelationship === 'direct_billed' && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Subscription active</span>
                <span className='text-foreground'>{client.hasSubscriptionActive ? 'Yes' : 'No'}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-base'>Plan & renewal</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            {schedule || renewal ? (
              <>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Plan type</span>
                  <span className='text-foreground'>{planTypeLabel(schedule?.planType || renewal?.planType, client)}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Price</span>
                  <span className='text-foreground'>
                    {resolveClientPriceLabel(client, schedule || renewal, masterPerReportFeeVal, currency)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Commitment ends</span>
                  <span className='text-foreground'>
                    {renewal?.commitmentEndDate || resolveCommitmentEndLabel(client, schedule)}
                  </span>
                </div>
                {renewal?.daysUntilCommitmentEnd != null && (
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground'>Days until end</span>
                    <span className='text-foreground'>{renewal.daysUntilCommitmentEnd}</span>
                  </div>
                )}
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Auto-renew</span>
                  <span className='text-foreground'>{(schedule || renewal)?.autoRenew !== false ? 'Yes' : 'No'}</span>
                </div>
              </>
            ) : (
              <p className='text-muted-foreground'>
                No billing schedule on file.{' '}
                {!client.consultantClientScheduleId &&
                  'Use Onboard existing in the portfolio or contact CYF admin to set commitment dates.'}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className='bg-card md:col-span-2'>
          <CardHeader>
            <CardTitle className='text-base'>Report access</CardTitle>
          </CardHeader>
          <CardContent>
            {usable ? (
              <p className='text-success text-sm'>This client can generate reports.</p>
            ) : (
              <p className='text-warning text-sm'>{unusableReason}</p>
            )}
            {client.billingRelationship === 'consultant_billed' &&
              (schedule?.planType === 'per_report' || renewal?.planType === 'per_report' || !client.billingRelationship) && (
              <p className='text-muted-foreground text-xs mt-2'>
                Per-report fees are billed to your consultant account when reports are generated.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setCurrentOrganisation: (organisation) => dispatch(ORG_ACTIONS.setCurrentOrganisation(organisation)),
});

export default connect(null, mapDispatchToProps)(ConsultantClientDetail);
